/**
 * 外部素材清单（单一来源）：界面“素材署名”面板、测试、docs/素材清单.md 都以此为准。
 * 授权核实：2026-10-12 访问 https://www.solarsystemscope.com/textures/ ，页面声明
 * “Distributed under Attribution 4.0 International license: You may use, adapt, and share these textures for any purpose, even commercially.”
 * 页面同时说明：纹理基于 NASA 高程与影像数据，并按 Messenger、Viking、Cassini 与哈勃的真彩照片调色。
 */
export const SSS_PAGE = "https://www.solarsystemscope.com/textures/";
export const LICENSE = { name: "CC BY 4.0", url: "https://creativecommons.org/licenses/by/4.0/" };

export interface TextureAsset { id: string; file: string; label: string; bytes: number; note?: string }
export const TEXTURE_ASSETS: readonly TextureAsset[] = [
  { id: "mercury", file: "2k_mercury.jpg", label: "水星 Mercury", bytes: 872555 },
  { id: "venus", file: "2k_venus_atmosphere.jpg", label: "金星大气 Venus atmosphere", bytes: 229696 },
  { id: "earth", file: "2k_earth_daymap.jpg", label: "地球白昼 Earth day", bytes: 463087 },
  { id: "earth-night", file: "2k_earth_nightmap.jpg", label: "地球夜景 Earth night", bytes: 255287 },
  { id: "earth-clouds", file: "2k_earth_clouds.jpg", label: "地球云层 Earth clouds", bytes: 965676 },
  { id: "earth-spec", file: "2k_earth_specular_map.jpg", label: "地球海洋高光 Earth specular", bytes: 291831, note: "原为 TIFF，转换为 JPEG（85%）" },
  { id: "moon", file: "2k_moon.jpg", label: "月球 Moon", bytes: 1053869 },
  { id: "mars", file: "2k_mars.jpg", label: "火星 Mars", bytes: 750547 },
  { id: "jupiter", file: "2k_jupiter.jpg", label: "木星 Jupiter", bytes: 498976 },
  { id: "saturn", file: "2k_saturn.jpg", label: "土星 Saturn", bytes: 199916 },
  { id: "saturn-ring", file: "2k_saturn_ring_alpha.png", label: "土星环 Saturn rings", bytes: 12119 },
  { id: "uranus", file: "2k_uranus.jpg", label: "天王星 Uranus", bytes: 77751 },
  { id: "neptune", file: "2k_neptune.jpg", label: "海王星 Neptune", bytes: 241580 },
  { id: "sun", file: "2k_sun.jpg", label: "太阳 Sun", bytes: 822427 },
  { id: "milkyway", file: "8k_stars_milky_way.jpg", label: "银河 Milky Way", bytes: 1905513, note: "银道坐标，运行时重投影到黄道坐标" },
];
export const textureUrl = (file: string) => `${import.meta.env.BASE_URL}textures/${file}`;
export const assetByid = (id: string) => TEXTURE_ASSETS.find((a) => a.id === id);

/** 没有真实素材、用程序生成示意贴图的天体（SSS 对这些只有“虚构”版本，不使用）。 */
export const PROCEDURAL_ONLY = ["pluto", "ceres", "haumea", "makemake", "eris", "halley"] as const;

export const CREDITS = [
  { name: "Solar System Scope textures", by: "INOVE / Solar System Scope", license: LICENSE, url: SSS_PAGE, desc: "行星、月球、太阳与银河贴图；基于 NASA 高程与影像数据", descEn: "Planet, Moon, Sun and Milky Way textures, based on NASA elevation and imagery data" },
  { name: "astronomy-engine", by: "Don Cross", license: { name: "MIT", url: "https://github.com/cosinekitty/astronomy" }, url: "https://github.com/cosinekitty/astronomy", desc: "行星、月球位置与自转轴", descEn: "Planet and Moon positions and rotation axes" },
  { name: "JPL Horizons", by: "NASA/JPL Solar System Dynamics", license: { name: "NASA/JPL（公开数据）", url: "https://ssd.jpl.nasa.gov/" }, url: "https://ssd.jpl.nasa.gov/horizons/", desc: "矮行星与哈雷彗星星历表；测试对照数据", descEn: "Dwarf-planet and Halley ephemeris tables; test reference data" },
] as const;
